import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { filter, finalize, Observable } from 'rxjs';

import { AuthStore } from '@falcon/app/core/storage/auth/auth.store';
import { RootStore } from '@falcon/app/core/storage/root/root.store';
import { environment } from '@falcon/env';
import { UserDetailsModel } from '@falcon/app/shared/models/user.model';
import { AppRoutePath } from '@falcon/app/app.routing.model';
import type { AuthResultModel } from './models/auth-result.model';
import { ConfirmPasswordModel } from './models/confirm-password.model';
import { LoginDataModel } from './models/login-data.model';
import { NewPasswordDataModel } from './models/new-password-data.model';
import { ResetPasswordResponseModel } from './models/reset-password-response.model';
import { DialogService } from '@falcon/app/shared/services/dialog/dialog.service';
import { switchMap } from 'rxjs/operators';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

export const AUTH_COOKIE_KEY = 'skey';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private rootStore = inject(RootStore);
  private authStore = inject(AuthStore);
  private router = inject(Router);
  private dialogService = inject(DialogService);

  private readonly CURRENT_USER_API_URL = `${environment.apiURL}/users/currentuser`;
  readonly AUTH_API_URL = `${environment.publicApiURL}/customers/companies/auth`;

  isAuthenticated(): boolean {
    return !!localStorage.getItem(AUTH_COOKIE_KEY);
  }

  login(loginData: LoginDataModel): Observable<AuthResultModel> {
    return this.http.post<AuthResultModel>(`${this.AUTH_API_URL}/login`, loginData);
  }

  setNewPassword(newPasswordData: NewPasswordDataModel): Observable<AuthResultModel> {
    return this.http.post<AuthResultModel>(`${this.AUTH_API_URL}/setPassword`, newPasswordData);
  }

  resetPassword(username: string): Observable<ResetPasswordResponseModel> {
    return this.http.post<ResetPasswordResponseModel>(`${this.AUTH_API_URL}/resetForgotPassword`, {
      username,
    });
  }

  confirmPassword(confirmPasswordData: ConfirmPasswordModel): Observable<void> {
    return this.http.post<void>(`${this.AUTH_API_URL}/confirmForgotPassword`, confirmPasswordData);
  }

  logoutCognito() {
    return this.http.get<void>(`${environment.apiURL}/customers/auth/logout`);
  }

  logout(): void {
    this.dialogService
      .openGenericConfirmDialog('SHARED.LOGOUT_CONFIRM_DIALOG', {
        data: { iconName: 'warning_circle' },
      })
      .pipe(
        filter(Boolean),
        switchMap(() => this.logoutCognito())
      )
      .subscribe(() => this.clearSession());
  }

  forceLogout(): void {
    this.logoutCognito()
      .pipe(finalize(() => this.clearSession()))
      .subscribe({ error: () => undefined });
  }

  private clearSession(): void {
    // The session key has to go before navigating: `/auth` is behind `authorizedGuard`, which
    // bounces still-authenticated users to `/search` (and from there to their company).
    localStorage.removeItem(AUTH_COOKIE_KEY);
    this.rootStore.clean();
    this.authStore.clean();
    this.router.navigateByUrl(AppRoutePath.AUTH);

    if (Capacitor.isNativePlatform()) {
      Browser.open({ url: environment.itsMeLogoutURL });
    }
  }

  loadCurrentUser(): void {
    this.http
      .get<UserDetailsModel>(this.CURRENT_USER_API_URL)
      .subscribe(user => this.authStore.setUser(user));
  }
}
